import type { SimStock, SimulationState } from "@/hooks/useSimulator";
import { ShieldAlert, ShieldCheck, Shield } from "lucide-react";

interface Props {
  simulation: SimulationState;
  stocks: SimStock[];
  portfolioValue: number;
  netWorth: number;
}

interface RiskMetric {
  label: string;
  value: string;
  score: number;
  description: string;
}

const PortfolioRiskAnalyzer = ({ simulation: sim, stocks, portfolioValue, netWorth }: Props) => {
  const holdings = Object.entries(sim.holdings);
  const hasHoldings = holdings.length > 0;

  const sectorCount = new Set(
    holdings.map(([ticker]) => stocks.find((s) => s.ticker === ticker)?.sector).filter(Boolean)
  ).size;
  const totalSectors = new Set(stocks.map((s) => s.sector)).size;
  const diversScore = hasHoldings ? Math.min((sectorCount / totalSectors) * 100, 100) : 0;

  let maxConcentration = 0;
  let maxConcentrationTicker = "";
  if (hasHoldings && portfolioValue > 0) {
    for (const [ticker, h] of holdings) {
      const val = (sim.stock_prices[ticker] || 0) * h.shares;
      const pct = (val / portfolioValue) * 100;
      if (pct > maxConcentration) {
        maxConcentration = pct;
        maxConcentrationTicker = ticker;
      }
    }
  }
  const concentrationScore = hasHoldings ? Math.max(0, 100 - maxConcentration) : 100;

  const cashRatio = netWorth > 0 ? (Number(sim.cash_balance) / netWorth) * 100 : 100;
  const cashScore =
    cashRatio >= 10 && cashRatio <= 30 ? 100 :
    cashRatio < 10 ? cashRatio * 10 :
    Math.max(0, 100 - (cashRatio - 30) * 2);

  let weightedVol = 0;
  if (hasHoldings && portfolioValue > 0) {
    for (const [ticker, h] of holdings) {
      const stock = stocks.find((s) => s.ticker === ticker);
      const val = (sim.stock_prices[ticker] || 0) * h.shares;
      const weight = val / portfolioValue;
      weightedVol += (stock?.volatility_rating || 5) * weight;
    }
  }
  const volScore = hasHoldings ? Math.max(0, 100 - weightedVol * 10) : 100;

  const netWorthHistory = sim.round_summaries.map((s) => s.netWorth);
  let maxDrawdown = 0;
  if (netWorthHistory.length > 0) {
    let peak = netWorthHistory[0];
    for (const nw of netWorthHistory) {
      if (nw > peak) peak = nw;
      const dd = ((peak - nw) / peak) * 100;
      if (dd > maxDrawdown) maxDrawdown = dd;
    }
  }
  const drawdownScore = Math.max(0, 100 - maxDrawdown * 3);

  let beta = 1;
  if (sim.round_summaries.length >= 3) {
    const portfolioReturns: number[] = [];
    const marketReturns: number[] = [];
    for (let i = 1; i < sim.round_summaries.length; i++) {
      const pPrev = sim.round_summaries[i - 1].netWorth;
      const pCurr = sim.round_summaries[i].netWorth;
      portfolioReturns.push((pCurr - pPrev) / pPrev);
      const mPrev = sim.hw500_history[i - 1]?.level || 1000;
      const mCurr = sim.hw500_history[i]?.level || 1000;
      marketReturns.push((mCurr - mPrev) / mPrev);
    }
    const avgP = portfolioReturns.reduce((s, v) => s + v, 0) / portfolioReturns.length;
    const avgM = marketReturns.reduce((s, v) => s + v, 0) / marketReturns.length;
    let cov = 0;
    let varM = 0;
    for (let i = 0; i < portfolioReturns.length; i++) {
      cov += (portfolioReturns[i] - avgP) * (marketReturns[i] - avgM);
      varM += (marketReturns[i] - avgM) ** 2;
    }
    beta = varM > 0 ? cov / varM : 1;
  }
  const betaScore = Math.max(0, 100 - Math.abs(beta - 1) * 50);

  const metrics: RiskMetric[] = [
    { label: "Diversification", value: `${sectorCount}/${totalSectors} sectors`, score: diversScore, description: "Spread across sectors" },
    { label: "Concentration", value: maxConcentrationTicker ? `${maxConcentration.toFixed(0)}% in ${maxConcentrationTicker}` : "N/A", score: concentrationScore, description: "Max single-stock exposure" },
    { label: "Cash Buffer", value: `${cashRatio.toFixed(0)}%`, score: cashScore, description: "Liquidity safety net" },
    { label: "Volatility Exposure", value: `${weightedVol.toFixed(1)}/10`, score: volScore, description: "Portfolio risk level" },
    { label: "Max Drawdown", value: `${maxDrawdown.toFixed(1)}%`, score: drawdownScore, description: "Largest peak-to-trough drop" },
    { label: "Beta", value: beta.toFixed(2), score: betaScore, description: "Market correlation (1.0 = market)" },
  ];

  const overallScore = Math.round(metrics.reduce((s, m) => s + m.score, 0) / metrics.length);

  const riskLevel =
    overallScore >= 80 ? { label: "Conservative", color: "text-emerald-400", icon: ShieldCheck } :
    overallScore >= 50 ? { label: "Moderate", color: "text-amber-400", icon: Shield } :
    { label: "Aggressive", color: "text-red-400", icon: ShieldAlert };

  const RiskIcon = riskLevel.icon;

  function getBarColor(score: number): string {
    if (score >= 70) return "bg-emerald-400/60";
    if (score >= 40) return "bg-amber-400/60";
    return "bg-red-400/60";
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-heading font-bold text-sm text-foreground/90 mb-1">Portfolio Risk Analysis</h3>
        <p className="text-muted-foreground text-[10px]">Comprehensive risk assessment</p>
      </div>

      <div className="rounded-xl border border-border bg-muted/30 p-4 text-center">
        <RiskIcon className={`w-8 h-8 mx-auto mb-2 ${riskLevel.color}`} />
        <p className={`font-heading font-bold text-2xl ${riskLevel.color}`}>{overallScore}</p>
        <p className={`font-heading font-semibold text-sm ${riskLevel.color}`}>{riskLevel.label}</p>
        <p className="text-muted-foreground/50 text-[10px] mt-1">Risk Score (0-100, higher = safer)</p>
      </div>

      <div className="space-y-2">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl border border-border/50 bg-muted/20 p-3">
            <div className="flex items-center justify-between mb-1">
              <div>
                <span className="text-foreground/80 text-xs font-heading font-semibold">{m.label}</span>
                <p className="text-muted-foreground/50 text-[10px]">{m.description}</p>
              </div>
              <span className="text-foreground font-heading font-bold text-sm">{m.value}</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getBarColor(m.score)}`}
                style={{ width: `${m.score}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {hasHoldings && (
        <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
          <p className="text-muted-foreground text-[10px] mb-2">💡 Recommendations</p>
          <div className="space-y-1 text-[11px] text-muted-foreground">
            {diversScore < 60 && <p>• Consider diversifying into more sectors</p>}
            {maxConcentration > 40 && <p>• Reduce concentration in {maxConcentrationTicker} (over 40%)</p>}
            {cashRatio < 10 && <p>• Low cash buffer — consider holding more cash for opportunities</p>}
            {cashRatio > 50 && <p>• High cash position — consider investing more to maximize returns</p>}
            {weightedVol > 7 && <p>• High volatility exposure — mix in lower-volatility stocks</p>}
            {maxDrawdown > 15 && <p>• Significant drawdown detected — review position sizing</p>}
            {Math.abs(beta) > 1.5 && <p>• Portfolio is more volatile than the market — consider rebalancing</p>}
            {overallScore >= 70 && <p>• Portfolio risk profile looks well-managed! 👍</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioRiskAnalyzer;
